import React, { FC, HTMLAttributes, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form } from 'react-final-form';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';

import { t } from 'utils';
import { RootState } from 'store/rootReducer';
import { Button, Heading } from 'components/ui';
import { DropdownWrapper } from 'components/forms';
import BasicModal from 'components/modals/BasicModal/BasicModal';
import { AppsConstants } from 'modules/userApps/store/constants';
import { useDispatchRequest, useQuery } from '@redux-requests/react';
import { supportOptions } from 'modules/userApps/AppDistribution/constants';
import { editAppPlan, toggleEditPlanModal } from 'modules/layout/store/actions';

import styles from './EditPlanModal.module.scss';

export interface IEditPlanModal extends HTMLAttributes<HTMLDivElement> {}

const EditPlanModal: FC<IEditPlanModal> = (props) => {
  const { className } = props;
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const disaptchRequest = useDispatchRequest();
  const isOpen = useSelector((state: RootState) => state.layout.editPlanModal);
  const {
    data: { plan },
  } = useQuery({ type: AppsConstants.APP, requestKey: id });
  const rootClassName = classNames(styles.root, className);

  const handleCloseModal = useCallback(() => {
    dispatch(toggleEditPlanModal(false));
  }, [dispatch]);

  const handleScopesSubmit = useCallback(
    async ({ plan = 'CLASSIC' }) => {
      const { data } = await disaptchRequest(editAppPlan({ plan, id }));
      if (data) {
        handleCloseModal();
      }
    },
    [disaptchRequest, handleCloseModal, id]
  );

  return (
    <BasicModal className={rootClassName} open={isOpen} onClose={handleCloseModal}>
      <Form
        initialValues={{ plan }}
        onSubmit={handleScopesSubmit}
        render={({ handleSubmit, valid, pristine }) => (
          <form onSubmit={handleSubmit}>
            <Heading className='mb-3' icon='boxes'>
              {t('application.plans.edit.heading')}
            </Heading>
            <Field
              name='plan'
              component={DropdownWrapper}
              label='application.plans.edit.label'
              placeholder='application.plans.edit.placeholder'
              options={supportOptions}
              className='mb-3'
              isShowSelected
            />
            <Button
              className='mr-2'
              inline
              type='button'
              variant='secondary'
              onClick={handleCloseModal}
            >
              {t('cancel')}
            </Button>
            <Button disabled={!valid || pristine} inline>
              {t('save.changes')}
            </Button>
          </form>
        )}
      />
    </BasicModal>
  );
};

export default EditPlanModal;
